// src/pages/EasaSeoPage.jsx

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

const EasaSeoPage = () => {
  const appName = "Part-66 Exam Pro"; // Uygulama adı

  return (
    <section>
      <div className="text-foreground min-h-screen pt-20">
        <div className="container mx-auto max-w-4xl p-4 py-8 md:p-8">
          <Card>
            <CardHeader>
              <CardTitle className="text-3xl md:text-4xl font-bold tracking-tight">
                Your Ultimate Guide to the EASA Part 66 License in 2025
              </CardTitle>
              <CardDescription className="pt-2">
                Ace Your Exams with {appName}: Your essential guide to becoming
                a licensed aircraft maintenance technician in the EU.
              </CardDescription>
            </CardHeader>
            <Separator className="mb-6" />
            <CardContent className="space-y-6 text-muted-foreground leading-7">
              <p>
                The dream of working hands-on with the world's most advanced
                aircraft is a powerful one. For technicians and engineers across
                the European Union, this dream is realized through a crucial
                qualification: the EASA Part 66 Aircraft Maintenance License
                (AML). This license is your passport to a rewarding career,
                demonstrating your expertise and commitment to the highest
                safety standards in aviation.
              </p>
              <p>
                Navigating the path to certification can be challenging, with a
                comprehensive syllabus and rigorous exams. But with the right
                preparation and tools, success is within your reach. This guide
                will demystify the EASA Part 66 license and show you how the{" "}
                <strong>{appName}</strong> app can be your essential study
                partner.
              </p>

              <section>
                <h2 className="text-2xl font-semibold mt-8 mb-4 text-foreground">
                  What is an EASA Part 66 Aircraft Maintenance License (AML)?
                </h2>
                <p>
                  An EASA Part 66 AML is a standardized European license
                  recognized in all EU member states, certifying that a
                  technician has the necessary knowledge and experience to
                  maintain and release aircraft back into service. It is the
                  benchmark qualification for professionals who ensure the
                  airworthiness of an aircraft, making it a legal requirement
                  for certifying maintenance work. Holding this license opens up
                  a world of opportunities, with EASA-certified engineers in
                  high demand globally.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mt-8 mb-4 text-foreground">
                  The Role of the Licensed Aircraft Maintenance Technician
                </h2>
                <p>
                  An aircraft maintenance technician or engineer is responsible
                  for the safety and reliability of an aircraft. Their duties
                  involve inspecting, repairing, overhauling, and testing
                  aircraft and their systems. It is a profession that demands
                  precision, in-depth technical knowledge, and a profound sense
                  of responsibility. The EASA Part 66 license is the formal
                  recognition of this competence.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mt-8 mb-4 text-foreground">
                  Understanding the EASA Part 66 License Categories
                </h2>
                <p>
                  The Part 66 license is structured into several categories,
                  each defining the scope of work the holder is certified to
                  perform. Your career goals will determine which path you
                  choose.
                </p>
                <ul className="list-disc pl-6 space-y-3 mt-4">
                  <li>
                    <strong className="text-foreground">
                      Category A: Line Maintenance Certifying Mechanic.
                    </strong>{" "}
                    Authorizes the holder to perform and certify minor scheduled
                    line maintenance and simple defect rectifications.
                  </li>
                  <li>
                    <strong className="text-foreground">
                      Category B1: Maintenance Certifying Technician
                      (Mechanical).
                    </strong>{" "}
                    The standard license for mechanical technicians, covering
                    aircraft structures, powerplants, and mechanical/electrical
                    systems. Subcategories include B1.1 (Aeroplanes Turbine),
                    B1.2 (Aeroplanes Piston), B1.3 (Helicopters Turbine), and
                    B1.4 (Helicopters Piston).
                  </li>
                  <li>
                    <strong className="text-foreground">
                      Category B2: Maintenance Certifying Technician (Avionics).
                    </strong>{" "}
                    The license for avionics specialists, covering all avionic
                    and electrical systems.
                  </li>
                  <li>
                    <strong className="text-foreground">Category B2L:</strong>{" "}
                    Similar to B2 but limited to specific system ratings like
                    'communication/navigation'.
                  </li>
                  <li>
                    <strong className="text-foreground">Category B3:</strong>{" "}
                    For maintenance on piston-engine non-pressurised aeroplanes
                    of 2000 kg MTOM and below.
                  </li>
                  <li>
                    <strong className="text-foreground">
                      Category C: Base Maintenance Certifying Engineer.
                    </strong>{" "}
                    This senior license allows the holder to issue certificates
                    of release to service for an entire aircraft after complex
                    base maintenance.
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mt-8 mb-4 text-foreground">
                  The EASA Part 66 Examination Modules: Your Syllabus for
                  Success
                </h2>
                <p>
                  To achieve your license, you must pass a series of exams based
                  on a set of core knowledge modules. {appName} is structured to
                  cover every single one of these essential topics.
                </p>
                <ul className="list-disc pl-6 space-y-3 mt-4">
                  <li>
                    <strong className="text-foreground">Module 1:</strong>{" "}
                    Mathematics
                  </li>
                  <li>
                    <strong className="text-foreground">Module 2:</strong>{" "}
                    Physics
                  </li>
                  <li>
                    <strong className="text-foreground">Module 3:</strong>{" "}
                    Electrical Fundamentals
                  </li>
                  <li>
                    <strong className="text-foreground">Module 4:</strong>{" "}
                    Electronic Fundamentals
                  </li>
                  <li>
                    <strong className="text-foreground">Module 5:</strong>{" "}
                    Digital Techniques/Electronic Instrument Systems
                  </li>
                  <li>
                    <strong className="text-foreground">Module 6:</strong>{" "}
                    Materials and Hardware
                  </li>
                  <li>
                    <strong className="text-foreground">Module 7:</strong>{" "}
                    Maintenance Practices
                  </li>
                  <li>
                    <strong className="text-foreground">Module 8:</strong> Basic
                    Aerodynamics
                  </li>
                  <li>
                    <strong className="text-foreground">Module 9:</strong> Human
                    Factors
                  </li>
                  <li>
                    <strong className="text-foreground">Module 10:</strong>{" "}
                    Aviation Legislation
                  </li>
                  <li>
                    <strong className="text-foreground">Module 11:</strong>{" "}
                    Aeroplane Aerodynamics, Structures and Systems
                  </li>
                  <li>
                    <strong className="text-foreground">Module 12:</strong>{" "}
                    Helicopter Aerodynamics, Structures and Systems
                  </li>
                  <li>
                    <strong className="text-foreground">Module 13:</strong>{" "}
                    Aircraft Aerodynamics, Structures and Systems
                  </li>
                  <li>
                    <strong className="text-foreground">Module 14:</strong>{" "}
                    Propulsion
                  </li>
                  <li>
                    <strong className="text-foreground">Module 15:</strong> Gas
                    Turbine Engines
                  </li>
                  <li>
                    <strong className="text-foreground">Module 16:</strong>{" "}
                    Piston Engines
                  </li>
                  <li>
                    <strong className="text-foreground">Module 17:</strong>{" "}
                    Propeller
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mt-8 mb-4 text-foreground">
                  Prepare Smarter, Not Harder, with {appName}
                </h2>
                <p>
                  The breadth and depth of the EASA Part 66 syllabus can feel
                  overwhelming. Success requires structured study, consistent
                  practice, and a reliable source of information. This is where{" "}
                  <strong>{appName}</strong> becomes your indispensable tool.
                </p>
                <p className="font-semibold text-foreground mt-4">
                  Key Features of {appName}:
                </p>
                <ul className="list-disc pl-6 space-y-3 mt-4">
                  <li>
                    <strong className="text-foreground">
                      Comprehensive Question Bank:
                    </strong>{" "}
                    Practice with hundreds of questions covering every subtopic
                    of all 17 EASA modules, tailored to your specific license
                    category (A, B1, B2, B3).
                  </li>
                  <li>
                    <strong className="text-foreground">
                      Realistic Exam Simulations:
                    </strong>{" "}
                    Get accustomed to the pressure of the real thing. Our timed
                    mock exams mirror the official test format, number of
                    questions, and time allowed for each module.
                  </li>
                  <li>
                    <strong className="text-foreground">
                      Detailed Explanations:
                    </strong>{" "}
                    Don't just learn the right answer—understand the "why"
                    behind it. Every question comes with a clear, concise
                    explanation to reinforce your knowledge.
                  </li>
                  <li>
                    <strong className="text-foreground">
                      Track Your Progress:
                    </strong>{" "}
                    Identify your strengths and weaknesses. Our app provides
                    detailed analytics on your performance, helping you focus
                    your study efforts where they are needed most.
                  </li>
                  <li>
                    <strong className="text-foreground">
                      Study Anytime, Anywhere:
                    </strong>{" "}
                    Turn your commute or downtime into productive study
                    sessions. With full offline access, your preparation
                    material is always at your fingertips.
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mt-8 mb-4 text-foreground">
                  Start Your Journey Today!
                </h2>
                <p>
                  Earning your EASA Part 66 Aircraft Maintenance License is a
                  significant achievement that opens the door to a secure and
                  exciting global career. The path requires dedication, but you
                  don't have to walk it alone.
                </p>
                <p className="font-bold text-foreground">
                  Download <strong>{appName}</strong> from the App Store today
                  and take the first step towards acing your exams and launching
                  your career in aircraft maintenance. Your future is cleared
                  for takeoff.
                </p>
              </section>

              <p className="mt-6 text-sm italic">
                Disclaimer: This application, {appName}, is not affiliated with,
                endorsed by, or sponsored by the European Union Aviation Safety
                Agency (EASA). It is designed as a supplementary study aid.
                Users are solely responsible for consulting official EASA source
                materials for their exam preparation.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default EasaSeoPage;
